import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { api, API_READY, youtubeId } from "../../lib/api";
import { SERMON_CATEGORIES } from "../../content/site";
import { MaskLines, Reveal } from "../motion";
import { Eyebrow } from "../Brand";

function Player({ s, vid }) {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <iframe
        className="absolute inset-0 h-full w-full"
        src={`https://www.youtube.com/embed/${vid}?autoplay=1&rel=0&playsinline=1`}
        title={s.title}
        referrerPolicy="strict-origin-when-cross-origin"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        data-testid="latest-sermon-iframe"
      />
    );
  }
  return (
    <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0 block h-full w-full" aria-label={`Play ${s.title}`} data-testid="latest-sermon-play-button">
      <img src={`https://i.ytimg.com/vi/${vid}/maxresdefault.jpg`} onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`; }} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
      <span className="absolute inset-0 bg-[#090909]/25 transition-colors duration-500 group-hover:bg-[#781C31]/25" />
      <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center bg-[#781C31] text-[#F6F2F0] transition-transform duration-300 group-hover:scale-110 sm:h-24 sm:w-24">
        <Play className="h-8 w-8 fill-current" aria-hidden="true" />
      </span>
    </button>
  );
}

export function LatestSermon() {
  const [s, setS] = useState(null);

  useEffect(() => {
    if (!API_READY) return undefined;
    let alive = true;
    api.get("/sermons")
      .then((r) => { const first = Array.isArray(r.data) ? r.data[0] : null; if (alive && first && youtubeId(first.youtube_url)) setS(first); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!s) return null;
  const vid = youtubeId(s.youtube_url);
  const cat = SERMON_CATEGORIES.find((c) => c.id === s.category)?.name ?? "";

  return (
    <section className="border-t border-[#F6F2F0]/10 bg-[#090909] py-24 lg:py-32" aria-labelledby="latest-title" data-testid="home-latest-sermon">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <Eyebrow>Latest Message</Eyebrow>
            <MaskLines as="h2" lines={[{ text: s.title, className: "text-[#F6F2F0]" }]} className="mt-6 font-display text-[14vw] font-bold uppercase leading-[0.88] tracking-tight sm:text-7xl lg:text-8xl" testId="latest-sermon-title" />
            <p id="latest-title" className="mt-5 font-display text-sm uppercase tracking-[0.25em] text-[#B9AFB2]" data-testid="latest-sermon-meta">
              {[s.scripture, cat].filter(Boolean).join(" · ")}
            </p>
          </div>
          <Reveal className="lg:col-span-4 lg:text-right">
            <Link to="/sermons" className="group inline-flex min-h-[48px] items-center gap-3 font-display text-sm uppercase tracking-[0.2em] text-[#F6F2F0] hover:text-[#B8324E]" data-testid="latest-sermon-vault-link">
              Enter The Vault <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <Reveal className="relative mt-12 aspect-video w-full overflow-hidden border border-[#F6F2F0]/10 bg-[#171416]">
          <Player s={s} vid={vid} />
        </Reveal>
        <a href={s.youtube_url} target="_blank" rel="noopener noreferrer" className="link-sweep mt-4 inline-flex min-h-[44px] items-center text-sm text-[#B9AFB2] hover:text-[#F6F2F0]" data-testid="latest-sermon-youtube-link">
          Trouble playing? Watch on YouTube
        </a>
      </div>
    </section>
  );
}
